import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Radio, Video, Trash2, ExternalLink, Youtube, Tv, Facebook, CheckCircle2, History,
} from 'lucide-react';
import { detectStreamUrl } from '../../lib/streamDetect';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/api';

const formatDuration = (mins) => {
  if (mins == null) return null;
  if (mins < 1) return '<1 min';
  if (mins < 60) return `${Math.round(mins)} min`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return m ? `${h}h ${m}m` : `${h}h`;
};

const formatStartTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
};

/**
 * Floating "Go Live" pill the coach taps to attach a live-stream URL to a game.
 * 3 visual states (priority high→low):
 *   1. URL set + ACTIVE  → red pulsing "● LIVE NOW"
 *   2. URL set + offline → green solid "Replay · Set"
 *   3. No URL set        → red "Go Live" with first-visit pulse
 */
export default function GoLiveButton({ gameId, currentUrl, currentActive, streamHistory = [], onSaved, className = '' }) {
  const [open, setOpen] = useState(false);
  const [draftUrl, setDraftUrl] = useState(currentUrl || '');
  const [draftActive, setDraftActive] = useState(!!currentActive);
  const [saving, setSaving] = useState(false);

  // Re-sync drafts whenever parent state changes (server save) or dialog re-opens.
  useEffect(() => { setDraftUrl(currentUrl || ''); }, [currentUrl, open]);
  useEffect(() => { setDraftActive(!!currentActive); }, [currentActive, open]);

  const detected = detectStreamUrl(draftUrl);
  const hasUrl = !!currentUrl;
  const isLiveNow = hasUrl && !!currentActive;

  const save = async ({ urlOverride, activeOverride } = {}) => {
    if (!gameId) return;
    const url = urlOverride !== undefined ? urlOverride : draftUrl.trim();
    const active = activeOverride !== undefined ? activeOverride : draftActive;
    if (url && !detectStreamUrl(url)) {
      toast.error('That URL doesn\'t look like a video stream link.');
      return;
    }
    setSaving(true);
    try {
      const resp = await api.put(`/games/${gameId}`, {
        live_stream_url: url,
        live_stream_active: active,
      });
      const successMsg = !url
        ? 'Live stream link removed.'
        : active
          ? '🔴 Broadcasting now — parents can watch live.'
          : 'Saved (offline). Flip the toggle on when you start broadcasting.';
      toast.success(successMsg);
      onSaved && onSaved({
        live_stream_url: resp.data?.live_stream_url ?? url,
        live_stream_active: resp.data?.live_stream_active ?? active,
        stream_history: resp.data?.stream_history ?? streamHistory,
      });
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Quick action: just toggle "live now" without opening the modal — used when URL is already set.
  const quickToggle = async () => {
    await save({ urlOverride: currentUrl, activeOverride: !currentActive });
  };

  const clearAll = async () => {
    if (!confirm('Remove the live stream link? This also closes any active broadcast.')) return;
    await save({ urlOverride: '', activeOverride: false });
  };

  // Pick what the floating button looks like based on state
  let label, icon, colorClasses;
  if (isLiveNow) {
    label = 'Live Now';
    icon = <span className="w-2 h-2 bg-white rounded-full animate-pulse shrink-0" />;
    colorClasses = 'bg-red-600 hover:bg-red-500 active:bg-red-700 text-white border-red-300 ring-2 ring-red-400/40';
  } else if (hasUrl) {
    label = 'Replay · Set';
    icon = <CheckCircle2 size={14} className="shrink-0" />;
    colorClasses = 'bg-emerald-500 hover:bg-emerald-400 active:bg-emerald-600 text-white border-emerald-300';
  } else {
    label = 'Go Live';
    icon = <Radio size={14} className="shrink-0" />;
    colorClasses = 'bg-red-500 hover:bg-red-400 active:bg-red-600 text-white border-red-300 ds-quicktips-pulse';
  }

  // Sort history newest-first
  const historySorted = [...(streamHistory || [])]
    .filter(h => h && h.started_at)
    .sort((a, b) => (b.started_at || '').localeCompare(a.started_at || ''));

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'fixed top-3 right-[140px] z-40',
          'flex items-center gap-1.5 px-3 py-2 rounded-full',
          'font-bold text-xs uppercase tracking-wider shadow-lg border-2',
          'transition-transform hover:scale-105 active:scale-95',
          colorClasses,
          className
        )}
        data-testid="go-live-btn"
        aria-label={hasUrl ? (isLiveNow ? 'Currently broadcasting — manage' : 'Stream link set — toggle live') : 'Set up a live stream'}
      >
        {icon}
        {label}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-stone-50 max-w-md max-h-[90vh] overflow-y-auto" data-testid="go-live-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-stone-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Radio size={20} className="text-red-600" /> {hasUrl ? 'MANAGE LIVE STREAM' : 'GO LIVE'}
            </DialogTitle>
            <DialogDescription className="text-xs text-stone-500">
              Stream the game from any phone &amp; service of your choice. Parents see an embedded player on the
              <span className="font-semibold"> Parent View</span> page when you flip the toggle on.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Quick platform shortcuts — Twitch RECOMMENDED first (no follower requirement) */}
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-stone-500 mb-1.5 block">
                Step 1 · Start a live broadcast on your phone
              </Label>
              <p className="text-[11px] text-stone-600 mb-2 leading-snug">
                Don&apos;t have a stream yet? Pick a platform — Twitch is the easiest if you&apos;re new.
              </p>
              <div className="grid grid-cols-3 gap-2">
                {/* Twitch — RECOMMENDED, no follower minimum */}
                <a href="https://www.twitch.tv/signup" target="_blank" rel="noopener noreferrer"
                   className="bg-purple-50 border-2 border-purple-300 hover:bg-purple-100 rounded-lg p-2.5 flex flex-col items-center text-center text-[10px] font-bold text-purple-700 transition relative"
                   data-testid="go-live-tw">
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-purple-600 text-white text-[8px] font-extrabold px-1.5 py-0.5 rounded uppercase tracking-wider">Easiest</span>
                  <Tv size={20} className="mb-1" />Twitch
                </a>
                <a href="https://www.facebook.com/live/producer" target="_blank" rel="noopener noreferrer"
                   className="bg-blue-50 border border-blue-200 hover:bg-blue-100 rounded-lg p-2.5 flex flex-col items-center text-center text-[10px] font-bold text-blue-700 transition"
                   data-testid="go-live-fb"><Facebook size={20} className="mb-1" />Facebook Live</a>
                <a href="https://m.youtube.com/upload" target="_blank" rel="noopener noreferrer"
                   className="bg-red-50 border border-red-200 hover:bg-red-100 rounded-lg p-2.5 flex flex-col items-center text-center text-[10px] font-bold text-red-700 transition"
                   data-testid="go-live-yt"><Youtube size={20} className="mb-1" />YouTube Live</a>
              </div>

              {/* Per-platform expandable guides */}
              <details className="mt-2 group" data-testid="guide-twitch">
                <summary className="text-[11px] text-purple-700 cursor-pointer hover:text-purple-800 font-semibold flex items-center gap-1">
                  <Tv size={12} /> How to go live on Twitch (60-second setup — easiest!)
                </summary>
                <div className="bg-purple-50/40 p-2 rounded mt-1.5">
                  {/* Phone → QR shortcut: scan to land directly on Twitch's
                      mobile signup. iOS hits the App Store, Android hits Play
                      Store, installed devices open the Twitch app. Saves the
                      "find Twitch in the app store" step. */}
                  <div className="flex gap-3 items-start mb-2">
                    <div className="shrink-0 flex flex-col items-center gap-1 bg-white border border-purple-200 rounded p-1.5" data-testid="twitch-qr">
                      <img
                        src="https://api.qrserver.com/v1/create-qr-code/?size=120x120&margin=2&data=https%3A%2F%2Fm.twitch.tv%2Fsignup"
                        alt="QR code — scan with phone camera"
                        width="110"
                        height="110"
                        style={{ display: 'block' }}
                      />
                      <p className="text-[9px] uppercase tracking-wider text-purple-700 font-bold leading-none">Scan with phone</p>
                    </div>
                    <div className="flex-1 min-w-0 text-[11px] text-stone-700 leading-snug pt-1">
                      <p className="font-bold text-purple-900 mb-1 text-[12px]">📱 Fastest path:</p>
                      <p>Open your phone camera, point at the QR code → it&apos;ll open the Twitch app (or App Store / Play Store if you don&apos;t have it yet).</p>
                      <p className="text-[10px] text-emerald-700 italic mt-1.5">
                        ✓ No follower requirement · ✓ Works on any phone
                      </p>
                    </div>
                  </div>
                  <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-1">Or follow these steps:</p>
                  <ol className="text-[11px] text-stone-700 leading-relaxed pl-4 list-decimal space-y-1">
                    <li>Download the free <span className="font-semibold">Twitch app</span> from the App Store or Google Play.</li>
                    <li>Tap <span className="font-semibold">Sign up</span> — use your email or Google account. Takes 30 seconds.</li>
                    <li>Tap your profile photo → <span className="font-semibold">Go Live</span> → allow camera + mic.</li>
                    <li>Title it (e.g. <em>&quot;Yankees vs Tigers · 1pm&quot;</em>), tap <span className="font-semibold">Start Stream</span>.</li>
                    <li>Tap the <span className="font-semibold">share button</span> in the corner → copy your stream URL.</li>
                    <li>Paste the URL into the box below ↓ &amp; toggle &quot;Broadcasting now&quot; ON.</li>
                  </ol>
                </div>
              </details>

              <details className="mt-1.5 group" data-testid="guide-facebook">
                <summary className="text-[11px] text-blue-700 cursor-pointer hover:text-blue-800 font-semibold flex items-center gap-1">
                  <Facebook size={12} /> How to go live on Facebook (good if you already use FB)
                </summary>
                <ol className="text-[11px] text-stone-700 leading-relaxed mt-1.5 pl-4 list-decimal space-y-1 bg-blue-50/40 p-2 rounded">
                  <li>Open the <span className="font-semibold">Facebook</span> app on your phone.</li>
                  <li>Tap the <span className="font-semibold">+</span> button (top right) → <span className="font-semibold">Live video</span>.</li>
                  <li>Pick where to post (your timeline, the team page, or a parents group).</li>
                  <li>Add a title, tap <span className="font-semibold">Start live video</span>.</li>
                  <li>Once it&apos;s rolling, tap the <span className="font-semibold">share menu</span> → <span className="font-semibold">Copy link</span>.</li>
                  <li>Paste the URL into the box below ↓ &amp; toggle &quot;Broadcasting now&quot; ON.</li>
                </ol>
                <p className="text-[10px] text-blue-700 mt-1.5 italic px-2">
                  ✓ No setup required · ✓ Parents who follow your page get a notification automatically
                </p>
              </details>

              <details className="mt-1.5 group" data-testid="guide-youtube">
                <summary className="text-[11px] text-red-700 cursor-pointer hover:text-red-800 font-semibold flex items-center gap-1">
                  <Youtube size={12} /> How to go live on YouTube (requires 50+ subscribers)
                </summary>
                <ol className="text-[11px] text-stone-700 leading-relaxed mt-1.5 pl-4 list-decimal space-y-1 bg-red-50/40 p-2 rounded">
                  <li>Open the <span className="font-semibold">YouTube</span> app on your phone.</li>
                  <li>Tap the <span className="font-semibold">+</span> at the bottom → <span className="font-semibold">Go live</span>.</li>
                  <li>Pick &quot;Camera&quot;, give it a title (e.g. <em>&quot;Yankees vs Tigers · 1pm&quot;</em>), tap <span className="font-semibold">Next</span>.</li>
                  <li>Tap <span className="font-semibold">Start</span> when ready.</li>
                  <li>Tap the <span className="font-semibold">share button</span> &amp; copy the URL.</li>
                  <li>Paste the URL into the box below ↓ &amp; toggle &quot;Broadcasting now&quot; ON.</li>
                </ol>
                <p className="text-[10px] text-amber-700 mt-1.5 italic px-2">
                  ⚠ YouTube requires <span className="font-bold">50+ subscribers</span> to stream from a phone. If you don&apos;t have that, use Twitch or Facebook instead.
                </p>
              </details>

              <p className="text-[10px] text-stone-500 mt-2 italic leading-snug">
                Already streaming somewhere else (Vimeo, Instagram, custom RTMP)? Just paste the watch URL below — we&apos;ll auto-detect it.
              </p>
            </div>

            {/* Step 2: Paste URL */}
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-stone-500 mb-1.5 block">
                Step 2 · Paste the stream URL here
              </Label>
              <Input
                placeholder="https://www.youtube.com/live/abc123 (or any stream link)"
                value={draftUrl}
                onChange={(e) => setDraftUrl(e.target.value)}
                className="font-mono text-[12px]"
                data-testid="go-live-url-input"
              />
              {draftUrl && detected && (
                <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded p-2 text-[11px] text-emerald-700">
                  <CheckCircle2 size={12} className="inline mr-1" />
                  Recognized: <span className="font-bold">{detected.label}</span>
                  {!detected.embed_url && (
                    <span className="block text-amber-700 mt-0.5">
                      ⚠ This link can&apos;t be embedded — parents will see a &quot;Watch on {detected.label}&quot; button instead.
                    </span>
                  )}
                </div>
              )}
              {draftUrl && !detected && (
                <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-700">
                  Unrecognized link — try pasting from YouTube, Facebook Live, Twitch, Vimeo, or Instagram.
                </div>
              )}
            </div>

            {/* Step 3: Broadcasting toggle */}
            <div className="bg-white border border-stone-200 rounded-lg p-3" data-testid="go-live-active-toggle-card">
              <Label className="text-[10px] uppercase tracking-wider text-stone-500 mb-1.5 block">
                Step 3 · Tell parents you&apos;re broadcasting
              </Label>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-stone-900">
                    {draftActive ? '🔴 Live broadcast — parents can watch now' : 'Offline (replay only)'}
                  </p>
                  <p className="text-[11px] text-stone-500 leading-relaxed mt-0.5">
                    {draftActive
                      ? 'Parents see the embedded player on Parent View. Flip OFF when the game ends.'
                      : 'When OFF, parents see a small "Replay available" link instead of an embedded player.'}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={draftActive}
                  onClick={() => setDraftActive(!draftActive)}
                  disabled={!draftUrl.trim()}
                  className={cn(
                    'relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition disabled:opacity-40 disabled:cursor-not-allowed',
                    draftActive ? 'bg-red-500' : 'bg-stone-300'
                  )}
                  data-testid="go-live-active-toggle"
                >
                  <span className={cn(
                    'inline-block h-5 w-5 transform rounded-full bg-white shadow transition',
                    draftActive ? 'translate-x-6' : 'translate-x-1'
                  )} />
                </button>
              </div>
            </div>

            {/* Embedded preview */}
            {detected && detected.embed_url && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-stone-500 mb-1.5 block">
                  Preview (this is what parents will see)
                </Label>
                <div className="aspect-video w-full bg-black rounded-lg overflow-hidden">
                  <iframe
                    src={detected.embed_url}
                    title="Stream preview"
                    className="w-full h-full"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    data-testid="go-live-preview-iframe"
                  />
                </div>
              </div>
            )}

            {/* Stream history */}
            {historySorted.length > 0 && (
              <div className="bg-white border border-stone-200 rounded-lg p-3" data-testid="go-live-history">
                <Label className="text-[10px] uppercase tracking-wider text-stone-500 mb-1.5 flex items-center gap-1.5">
                  <History size={12} /> Past Broadcasts ({historySorted.length})
                </Label>
                <div className="space-y-1 max-h-36 overflow-y-auto">
                  {historySorted.slice(0, 8).map((h, i) => {
                    const dur = formatDuration(h.duration_minutes);
                    return (
                      <div key={i} className="flex items-center justify-between text-[11px] py-1 border-t border-stone-100 first:border-t-0">
                        <span className="text-stone-700 font-medium">{formatStartTime(h.started_at)}</span>
                        <span className="text-stone-500">
                          {h.ended_at ? (
                            dur ? `${dur}` : 'Closed'
                          ) : (
                            <span className="inline-flex items-center gap-1 text-red-600 font-bold">
                              <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" /> LIVE
                            </span>
                          )}
                          {h.watch_url && (
                            <a href={h.watch_url} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 hover:underline">
                              <ExternalLink size={10} className="inline" />
                            </a>
                          )}
                        </span>
                      </div>
                    );
                  })}
                  {historySorted.length > 8 && (
                    <p className="text-[10px] text-stone-400 text-center pt-1">+ {historySorted.length - 8} earlier</p>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-1">
              {hasUrl && (
                <Button type="button" variant="outline" onClick={clearAll} disabled={saving}
                        className="border-red-300 text-red-700 hover:bg-red-50" data-testid="go-live-remove-btn">
                  <Trash2 size={12} className="mr-1" /> Remove
                </Button>
              )}
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={saving} className="flex-1" data-testid="go-live-cancel-btn">
                Cancel
              </Button>
              <Button type="button" onClick={() => save()} disabled={saving}
                      className="flex-1 bg-red-600 hover:bg-red-500 text-white font-bold" data-testid="go-live-save-btn">
                {saving ? 'Saving…' : (hasUrl ? 'Update' : 'Save')}
              </Button>
            </div>

            {/* Quick "go live now" CTA when URL is already set but offline */}
            {hasUrl && !currentActive && (
              <Button
                type="button"
                onClick={quickToggle}
                disabled={saving}
                className="w-full bg-red-600 hover:bg-red-500 text-white font-extrabold text-sm uppercase tracking-wider"
                data-testid="go-live-quick-start-btn"
              >
                <Video size={14} className="mr-1.5" />
                Start broadcasting now
              </Button>
            )}
            {hasUrl && currentActive && (
              <Button
                type="button"
                onClick={quickToggle}
                disabled={saving}
                variant="outline"
                className="w-full border-stone-300"
                data-testid="go-live-quick-stop-btn"
              >
                <Video size={14} className="mr-1.5" />
                Stop broadcasting
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
